import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyCampaigns, type MyCampaign } from '../services/campaigns';

const MyCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<MyCampaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMyCampaigns();
        if (!cancelled) {
          setCampaigns(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load campaigns';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalFunded = !loading && !error && Array.isArray(campaigns) && campaigns.length > 0
    ? campaigns.reduce((sum, c) => sum + (typeof c.amount === 'number' ? c.amount : 0), 0)
    : 0;

  const filteredCampaigns = !query.trim()
    ? (campaigns || [])
    : (campaigns || []).filter((c) => {
        const q = query.trim().toLowerCase();
        const name = (c.packageName || '').toLowerCase();
        const ref = (c.id || '').toLowerCase();
        const adTitle = (c.adTitle || '').toLowerCase();
        return name.includes(q) || ref.includes(q) || adTitle.includes(q);
      });

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My campaigns</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Funding events tied to your account, along with the package and creative they support.
          </p>
          {!loading && !error && Array.isArray(campaigns) && campaigns.length > 0 && (
            <p className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-300">
              Total funded so far: <span className="font-semibold">USD {totalFunded.toFixed(2)}</span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by package, creative, or ref ID…"
            className="w-56 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <Link
            to="/campaign/start"
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Start a new campaign
          </Link>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading your campaigns…</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && (!Array.isArray(campaigns) || campaigns.length === 0) && (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-4 text-sm text-slate-600 dark:text-slate-400">
          <p>You don&apos;t have any funded campaigns yet.</p>
          <p className="mt-1">
            When you complete a payment from the Start a campaign flow, it will appear here with a
            reference ID and basic performance.
          </p>
        </div>
      )}

      {!loading && !error && Array.isArray(campaigns) && campaigns.length > 0 && (
        <div className="space-y-2">
          {filteredCampaigns.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-4 text-sm text-slate-600 dark:text-slate-400">
              <p>No campaigns match your search.</p>
              <p className="mt-1 text-xs">
                Try searching by package name, creative title, or reference ID.
              </p>
            </div>
          )}
          {filteredCampaigns.map((c) => {
            const created = c.createdAt ? new Date(c.createdAt) : null;
            const hasPerformance =
              (typeof c.views === 'number' && c.views > 0) ||
              (typeof c.clicks === 'number' && c.clicks > 0) ||
              (typeof c.socialClicks === 'number' && c.socialClicks > 0);

            return (
              <div
                key={c.id}
                className="flex flex-col gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm text-slate-800 dark:text-slate-200 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex flex-1 items-start gap-3">
                  {c.adThumbnailUrl ? (
                    <img
                      src={c.adThumbnailUrl}
                      alt={c.adTitle || 'Campaign creative'}
                      className="h-14 w-20 flex-shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="h-14 w-20 flex-shrink-0 rounded-md bg-slate-100 dark:bg-slate-800" />
                  )}
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {c.packageName || 'Campaign funding event'}
                      </span>
                      <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-700 dark:text-slate-300">
                        {c.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      {c.adTitle ? c.adTitle : 'No creative attached yet'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {created && !Number.isNaN(created.getTime())
                        ? created.toLocaleString()
                        : 'Time not available'}
                      {' · '}Ref ID
                      <span className="ml-1 font-mono text-[11px]">{c.id}</span>
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-slate-600 dark:text-slate-400">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {c.currency} {c.amount.toFixed(2)}
                  </span>
                  {hasPerformance ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      {typeof c.views === 'number' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">Views</span>
                          <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">{c.views}</span>
                        </span>
                      )}
                      {typeof c.clicks === 'number' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">Clicks</span>
                          <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">{c.clicks}</span>
                        </span>
                      )}
                      {typeof c.socialClicks === 'number' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">Shares</span>
                          <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">
                            {c.socialClicks}
                          </span>
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">No performance data yet.</span>
                  )}
                  <Link
                    to={`/campaigns/${c.id}/performance`}
                    className="mt-1 text-[11px] font-medium text-blue-600 hover:underline"
                  >
                    View performance details
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
};

export default MyCampaigns;
